import React, { useMemo, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend } from 'recharts';
import { format, parseISO, isValid, getDay } from 'date-fns';
import { Calendar, TrendingUp, Clock, ChevronDown, ChevronUp } from 'lucide-react';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { Badge } from '@/components/ui/badge';


interface WeekdayWeekendAnalyticsProps {
  incidents: any[];
  catalogTasks: any[];
  dateRange?: {
    from?: Date;
    to?: Date;
  };
}

interface TicketData {
  number: string;
  description: string;
  openedDate: string;
  type: 'incident' | 'task';
  priority?: string;
  sortDate: Date;
}

interface AnalyticsData {
  weekdays: {
    incidents: number;
    tasks: number;
    total: number;
  };
  weekends: {
    incidents: number;
    tasks: number;
    total: number;
  };
  dailyBreakdown: Array<{
    day: string;
    incidents: number;
    tasks: number;
    total: number;
    isWeekend: boolean;
  }>;
  ticketsByDay: Record<string, Array<{
    number: string;
    description: string;
    openedDate: string;
    type: 'incident' | 'task';
    priority?: string;
    sortDate: Date;
  }>>;
}

export function WeekdayWeekendAnalytics({ incidents, catalogTasks, dateRange }: WeekdayWeekendAnalyticsProps) {
  const [selectedDay, setSelectedDay] = useState<string | null>(null);
  const [isDetailsOpen, setIsDetailsOpen] = useState(false);
  
  const analyticsData = useMemo((): AnalyticsData => {
    const safeIncidents = Array.isArray(incidents) ? incidents.filter(Boolean) : [];
    const safeCatalogTasks = Array.isArray(catalogTasks) ? catalogTasks.filter(Boolean) : [];
    
    console.log("WeekdayWeekendAnalytics - Processing data:", {
      incidents: safeIncidents.length,
      catalogTasks: safeCatalogTasks.length,
      dateRange: dateRange ? `${dateRange.from} to ${dateRange.to}` : 'No date range'
    });
    
    // Initialize stats and daily breakdown
    let weekdayIncidents = 0, weekendIncidents = 0;
    let weekdayTasks = 0, weekendTasks = 0;
    
    const dailyData = {
      Monday: { incidents: 0, tasks: 0, total: 0 },
      Tuesday: { incidents: 0, tasks: 0, total: 0 },
      Wednesday: { incidents: 0, tasks: 0, total: 0 },
      Thursday: { incidents: 0, tasks: 0, total: 0 },
      Friday: { incidents: 0, tasks: 0, total: 0 },
      Saturday: { incidents: 0, tasks: 0, total: 0 },
      Sunday: { incidents: 0, tasks: 0, total: 0 }
    };

    const ticketsTracker: Record<string, TicketData[]> = {
      Monday: [], Tuesday: [], Wednesday: [], Thursday: [], 
      Friday: [], Saturday: [], Sunday: []
    };
    
    // Helper function to process tickets with timezone conversion
    // Initialize counters
    const weekdayData = { incidents: 0, tasks: 0, total: 0 };
    const weekendData = { incidents: 0, tasks: 0, total: 0 };
    const dailyCounts: Record<string, { incidents: number; tasks: number; total: number; isWeekend: boolean }> = {};
    const ticketsByDay: Record<string, Array<{
      number: string;
      description: string;
      openedDate: string;
      type: 'incident' | 'task';
      priority?: string;
      sortDate: Date;
    }>> = {};
    
    // Day names for display
    const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    
    // Initialize daily counts and ticket arrays
    dayNames.forEach((day, index) => {
      dailyCounts[day] = { 
        incidents: 0, 
        tasks: 0, 
        total: 0, 
        isWeekend: index === 0 || index === 6 // Sunday = 0, Saturday = 6
      };
      ticketsByDay[day] = [];
    });
    
    // Helper function to process tickets
    const processTickets = (tickets: any[], isIncident: boolean) => {
      let processedCount = 0;
      let invalidDateCount = 0;
      
      tickets.forEach(ticket => {
        const dateField = ticket['Opened'] || ticket['opened'] || ticket['opened_at'] || 
                          ticket['created'] || ticket['created_at'] || ticket['Created'] || ticket['Created_at'];
        if (!dateField) {
          return;
        }
        
        try {
          // Parse the date directly without timezone conversion
          let convertedDate: Date;
          if (typeof dateField === 'string') {
            if (dateField.includes('/') && (dateField.includes('AM') || dateField.includes('PM'))) {
              convertedDate = new Date(dateField);
            } else {
              convertedDate = parseISO(dateField);
            }
          } else {
            convertedDate = new Date(dateField);
          }
          
          if (!convertedDate || !isValid(convertedDate)) {
            invalidDateCount++;
            return;
          }
          
          // Check if date is within range if specified
          if (dateRange && dateRange.from && dateRange.to) {
            if (convertedDate < dateRange.from || convertedDate > dateRange.to) return;
          }
          
          const dayOfWeek = getDay(convertedDate); // 0 = Sunday, 6 = Saturday
          const dayName = dayNames[dayOfWeek];
          const isWeekend = dayOfWeek === 0 || dayOfWeek === 6;
          
          processedCount++;
          
          // Update daily counts
          if (isIncident) {
            dailyCounts[dayName].incidents++;
          } else {
            dailyCounts[dayName].tasks++;
          }
          dailyCounts[dayName].total++;
          
          // Update weekday/weekend totals
          if (isWeekend) {
            if (isIncident) {
              weekendData.incidents++;
            } else {
              weekendData.tasks++;
            }
            weekendData.total++;
          } else {
            if (isIncident) {
              weekdayData.incidents++;
            } else {
              weekdayData.tasks++;
            }
            weekdayData.total++;
          }
          
          // Add ticket to ticketsByDay with converted time
          const ticketData = {
            number: ticket['number'] || ticket['Number'] || '',
            description: ticket['short_description'] || ticket['description'] || ticket['Short description'] || '',
            openedDate: format(convertedDate, 'MMM d, yyyy HH:mm'),
            type: (isIncident ? 'incident' : 'task') as 'incident' | 'task',
            priority: ticket['priority'] || ticket['Priority'] || undefined,
            sortDate: convertedDate // Use converted date for sorting
          };
          ticketsByDay[dayName].push(ticketData);
        } catch (e) {
          console.error("Error processing ticket date:", e, dateField);
        }
      });
      
      console.log(`Processed ${processedCount} ${isIncident ? 'incidents' : 'tasks'}, ${invalidDateCount} had invalid dates`);
    };
    
    // Process incidents and tasks
    processTickets(safeIncidents, true);
    processTickets(safeCatalogTasks, false);
    
    // Sort tickets in each day by date/time (ascending order)
    Object.keys(ticketsByDay).forEach(day => {
      ticketsByDay[day].sort((a, b) => a.sortDate.getTime() - b.sortDate.getTime());
    });
    
    // Convert daily counts to array
    const dailyBreakdown = dayNames.map(day => ({
      day,
      ...dailyCounts[day]
    }));
    
    console.log("WeekdayWeekendAnalytics - Final results:", {
      weekdays: weekdayData,
      weekends: weekendData,
      dailyBreakdown
    });
    
    return {
      weekdays: weekdayData,
      weekends: weekendData,
      dailyBreakdown,
      ticketsByDay
    };
  }, [incidents, catalogTasks, dateRange]);
  
  // Prepare data for charts
  const comparisonData = [
    {
      period: 'Weekdays',
      incidents: analyticsData.weekdays.incidents,
      tasks: analyticsData.weekdays.tasks,
      total: analyticsData.weekdays.total
    },
    {
      period: 'Weekends',
      incidents: analyticsData.weekends.incidents,
      tasks: analyticsData.weekends.tasks,
      total: analyticsData.weekends.total
    }
  ];
  
  const pieData = [
    {
      name: 'Weekdays',
      value: analyticsData.weekdays.total,
      color: '#3b82f6'
    },
    {
      name: 'Weekends',
      value: analyticsData.weekends.total,
      color: '#ef4444'
    }
  ];
  
  // Calculate percentages
  const totalTickets = analyticsData.weekdays.total + analyticsData.weekends.total;
  const weekdayPercentage = totalTickets > 0 ? (analyticsData.weekdays.total / totalTickets * 100).toFixed(1) : '0';
  const weekendPercentage = totalTickets > 0 ? (analyticsData.weekends.total / totalTickets * 100).toFixed(1) : '0';
  
  // Calculate average per day
  const avgWeekdayTickets = (analyticsData.weekdays.total / 5).toFixed(1); // 5 weekdays
  const avgWeekendTickets = (analyticsData.weekends.total / 2).toFixed(1); // 2 weekend days
  
  // Handle bar click
  const handleBarClick = (data: any) => {
    if (data && data.activeLabel) {
      const clickedDay = data.activeLabel;
      setSelectedDay(clickedDay);
      setIsDetailsOpen(true);
    }
  };
  
  // Get tickets for selected day
  const selectedDayTickets = selectedDay ? analyticsData.ticketsByDay[selectedDay] || [] : [];
  
  // If no data available, show message
  if (totalTickets === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Weekday vs Weekend Analytics</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-center py-8 text-gray-500">
            <Calendar className="h-12 w-12 mx-auto mb-4 text-gray-300" />
            <p className="text-lg font-medium">No ticket data available</p>
            <p className="text-sm">
              {dateRange && dateRange.from && dateRange.to 
                ? `No tickets found for the selected date range (${format(dateRange.from, 'MMM d, yyyy')} - ${format(dateRange.to, 'MMM d, yyyy')})`
                : 'No tickets found in the current dataset'
              }
            </p>
            <p className="text-xs mt-2 text-gray-400">
              Make sure your incidents and catalog tasks have valid date fields (Opened, opened, created, etc.)
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }
  
  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Weekday Tickets</CardTitle>
            <Calendar className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-blue-600">{analyticsData.weekdays.total}</div>
            <p className="text-xs text-muted-foreground">
              {weekdayPercentage}% of total • Avg {avgWeekdayTickets} tickets per weekday over the given time period
            </p>
            <div className="text-xs text-muted-foreground mt-1">
              {analyticsData.weekdays.incidents} incidents • {analyticsData.weekdays.tasks} tasks
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Weekend Tickets</CardTitle>
            <Clock className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-red-600">{analyticsData.weekends.total}</div>
            <p className="text-xs text-muted-foreground">
              {weekendPercentage}% of total • Avg {avgWeekendTickets} per weekend day over the given time period
            </p>
            <div className="text-xs text-muted-foreground mt-1">
              {analyticsData.weekends.incidents} incidents • {analyticsData.weekends.tasks} tasks
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Weekend Impact</CardTitle>
            <TrendingUp className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {totalTickets > 0 ? (parseFloat(avgWeekendTickets) / parseFloat(avgWeekdayTickets) * 100).toFixed(0) : '0'}%
            </div>
            <p className="text-xs text-muted-foreground">
              Weekend vs weekday daily average
            </p>
            <div className="text-xs text-muted-foreground mt-1">
              {parseFloat(avgWeekendTickets) > parseFloat(avgWeekdayTickets) ? 'Higher' : 'Lower'} weekend activity
            </div>
          </CardContent>
        </Card>
      </div>
      
      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Bar Chart - Weekday vs Weekend Comparison */}
        {/* <Card>
          <CardHeader>
            <CardTitle>Weekday vs Weekend Distribution</CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={comparisonData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="period" />
                <YAxis />
                <Tooltip 
                  formatter={(value, name) => [value, name === 'incidents' ? 'Incidents' : 'Tasks']}
                />
                <Bar dataKey="incidents" fill="#ef4444" name="incidents" />
                <Bar dataKey="tasks" fill="#3b82f6" name="tasks" />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card> */}
        
        {/* Pie Chart - Overall Distribution */}
        {/* <Card>
          <CardHeader>
            <CardTitle>Overall Ticket Distribution</CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={pieData}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {pieData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip formatter={(value) => [value, 'Tickets']} />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          </CardContent>
        </Card> */}
      </div>
      
      {/* Daily Breakdown */}
      <Card>
        <CardHeader>
          <CardTitle>Daily Breakdown by Day of Week</CardTitle>
          <p className="text-sm text-muted-foreground">Click on any bar to see detailed ticket list for that day</p>
        </CardHeader>
        <CardContent>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart 
              data={analyticsData.dailyBreakdown}
              onClick={handleBarClick}
              style={{ cursor: 'pointer' }}
            >
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="day" />
              <YAxis />
              <Tooltip 
                formatter={(value, name) => [value, name === 'incidents' ? 'Incidents' : 'Tasks']}
              />
              <Bar 
                dataKey="incidents" 
                fill="#ef4444" 
                name="incidents"
              />
              <Bar 
                dataKey="tasks" 
                fill="#3b82f6" 
                name="tasks"
              />
            </BarChart>
          </ResponsiveContainer>
          
          {/* Ticket Details Section */}
          {selectedDay && (
            <div className="mt-6">
              <Collapsible open={isDetailsOpen} onOpenChange={setIsDetailsOpen}>
                <CollapsibleTrigger className="flex items-center justify-between w-full p-3 bg-muted rounded-lg hover:bg-muted/80 transition-colors">
                  <div className="flex items-center gap-2">
                    <Calendar className="h-4 w-4" />
                    <span className="font-medium">
                      {selectedDay} Tickets ({selectedDayTickets.length})
                    </span>
                  </div>
                  {isDetailsOpen ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                </CollapsibleTrigger>
                <CollapsibleContent className="mt-3">
                  {selectedDayTickets.length > 0 ? (
                    <div className="space-y-2">
                      {selectedDayTickets.map((ticket, index) => (
                        <div key={index} className="p-3 border rounded-lg bg-background">
                          <div className="flex items-start justify-between gap-3">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-1">
                                <Badge 
                                  variant={ticket.type === 'incident' ? 'destructive' : 'default'}
                                  className={ticket.type === 'incident' ? 'bg-red-500 hover:bg-red-600' : 'bg-blue-500 hover:bg-blue-600 text-white'}
                                >
                                  {ticket.type === 'incident' ? 'INC' : 'TASK'}
                                </Badge>
                                <span className="font-mono text-sm font-medium">{ticket.number}</span>
                                {ticket.priority && (
                                  <Badge variant="outline" className="text-xs">
                                    P{ticket.priority}
                                  </Badge>
                                )}
                              </div>
                              <p className="text-sm text-muted-foreground mb-1">{ticket.description}</p>
                              <p className="text-xs text-muted-foreground">Opened: {ticket.openedDate}</p>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-4 text-muted-foreground">
                      No tickets found for {selectedDay}
                    </div>
                  )}
                </CollapsibleContent>
              </Collapsible>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
} 